/**
 * Response filters
 */

import { Links } from "./types";
import FilterHook from "./FilterHook";
import wpFetch from "./wpFetch";
import { ApiAdapterMessage } from "@/api-adapters/ApiAdapter";

function linkMatchedStrings(markdown: string, dictionary: Links) {
	// console.dir(dictionary);
	// Escape special regex characters in the dictionary keys
	function escapeRegExp(string: string) {
		return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
	}

	// Sort dictionary keys by length in descending order to handle nested keys properly
	const sortedKeys = Object.keys(dictionary).sort((a, b) => b.length - a.length);

	// Build the regex pattern from the dictionary keys
	const pattern = new RegExp(sortedKeys.map(escapeRegExp).join("|"), "g");

	// Regex to identify existing links
	const linkPattern = /!\[([^\]]*)\]\(([^)]+)\)|\[[^\]]*\]\(([^)]+)\)/g;

	// Temporary placeholders for existing links
	let index = 0;
	const linkPlaceholders: { [key: string]: string } = {};

	// Replace existing links with placeholders.
	//
	// This is a UUIDv4 in Base64 (with non-alphanumeric characters replaced).
	// Probably overkill, but it should eliminate any realistic risk of replacing
	// unintended text.
	let tempMarkdown = markdown.replace(linkPattern, (match) => {
		const placeholder = `__azO634YqS9ulg17CwlrK1A__${index}__`;
		linkPlaceholders[placeholder] = match;
		index++;
		return placeholder;
	});

	// Replace matched keys with the markdown links
	tempMarkdown = tempMarkdown.replace(pattern, (matched: string) => {
		const link = dictionary[matched];
		if (link.title.length > 0) {
			return `[${matched}](${link.url} "${link.title}")`;
		} else {
			return `[${matched}](${link.url})`;
		}
	});

	// Reinsert the original links
	const finalMarkdown = tempMarkdown.replace(/__azO634YqS9ulg17CwlrK1A__\d+__/g, (placeholder) => {
		return linkPlaceholders[placeholder];
	});

	return finalMarkdown;
}

export async function linkCitations(
	message: ApiAdapterMessage,
	wpNonce: string,
	citationLinkCache: Links,
	setCitationLinkCache: React.Dispatch<React.SetStateAction<Links>>,
	extraLinks: Links = {},
): Promise<ApiAdapterMessage> {
	// Extract the text and annotations from the original message source
	let text: string = message.content;
	const links: Links = extraLinks;

	console.log("Linking citations…");
	console.dir(message.annotations);

	// Process each annotation to collect citations
	if (message.annotations.length > 0) {
		console.log("Found annotations…");

		// Filter annotations to only file citation annotations
		const citationAnnotations: any[] = message.annotations.filter(
			(annotation) => annotation.type === "file_citation",
		);

		console.log("citationAnnotations:");
		console.dir(citationAnnotations);

		if (citationAnnotations.length > 0) {
			console.log("Found citationAnnotations…");
			// Get array of unique file_ids
			const fileIds = Array.from(
				new Set(citationAnnotations.map((annotation) => annotation.file_id)),
			);

			console.log("fileIds:");
			console.dir(fileIds);

			// Fetch any uncached citations
			const uncachedFileIds = fileIds.filter((file_id) => !(file_id in citationLinkCache));
			if (uncachedFileIds.length > 0) {
				console.log("Found uncachedFileIds…");
				console.dir(uncachedFileIds);

				// Build route with query string
				const params = new URLSearchParams();

				uncachedFileIds.forEach((id) => {
					params.append("export_id[]", id);
				});

				// console.log(params.toString());

				// Fetch from API
				try {
					const newLinks = await wpFetch(`posts?${params.toString()}`, wpNonce);
					console.log("Fetched links from API:");
					console.dir(newLinks);
					citationLinkCache = { ...citationLinkCache, ...newLinks };
				} catch (error) {
					console.log("Error fetching links from API:");
					console.error(error);
				}
			} // if uncached citations

			// De-dupe links & convert codes to numbers
			const citationIndexMap: string[] = [];
			let indexOffset = 0;
			citationAnnotations.forEach((annotation) => {
				const file_id = annotation.file_id;
				if (file_id in citationLinkCache) {
					console.log("Found cached citation.");
					// Index matched citations
					if (!citationIndexMap.includes(file_id)) {
						citationIndexMap.push(file_id);
					}

					console.log("annotation.index: ", annotation.index);
					console.log("indexOffset: ", indexOffset);

					const adjustedIndex = annotation.index + indexOffset;
					const currentCitationTag = `【${citationIndexMap.indexOf(file_id) + 1}】`;
					text = text.slice(0, adjustedIndex) + currentCitationTag + text.slice(adjustedIndex);

					console.log("currentCitationTag.length: ", currentCitationTag.length);
					console.log("adjustedIndex: ", adjustedIndex);

					indexOffset += currentCitationTag.length;
					console.log("new indexOffset: ", indexOffset);

					// Add matched citations to links
					const { title, url } = citationLinkCache[file_id];

					// - Footnotes
					links[currentCitationTag] = {
						title: title,
						url: url,
					};

					// - Inline
					links[title] = {
						title: "",
						url: url,
					};
				} else {
					console.log("Citation not cached.");
					// Remove unmatched citations
					text = text.replace(annotation.text, "");
				} // if file_id in cache
			});

			// Remove duplicates from citation clusters
			text = removeDuplicateCitationsByCluster(text);
		} // if has citation annotations

		// Update state
		if (Object.keys(citationLinkCache).length > 0) {
			setCitationLinkCache(citationLinkCache);
		}
	} // if has annotations

	// Link text
	console.dir(links);
	if (Object.keys(links).length > 0) {
		text = linkMatchedStrings(text, links);
	} // if has links

	message.content = text;

	return message;
} // linkCitations()

function removeDuplicateCitationsByCluster(text: string): string {
	// Match clusters of consecutive citations like "【1】 【2】 【1】"
	const clusterRegex = /(【\d+】(?:\s?【\d+】)*)/g;

	// Process each cluster individually
	text = text.replace(clusterRegex, (cluster) => {
		// Split the cluster into individual citations
		const citations = cluster.match(/【\d+】/g) || [];

		// Deduplicate while preserving order
		const uniqueCitations = [...new Set(citations)];

		// Join the filtered citations back together
		return uniqueCitations.join("");
	});

	return text;
}

function removeCitations(text: string) {
	return text.replace(/\s*【.*?†.*?】\s*/g, "");
}

/**
 * Convert OpenAI's non-standard delimiters around LaTeX to the standard syntax
 * recognized by KaTeX.
 *
 * @param text The text to be filtered
 * @returns Filtered text
 */
function fixOpenAILatexDelimiters(text: string) {
	// Beware of escaping: \\ = \ and $$$ = $$.

	return text
		.replace(/\\[\[\]]/g, "\n$$$\n") // Block delimiters
		.replace(/\\[()]/g, "$$$"); // Inline delimiters
}

export const messageDeltaFilters = new FilterHook<string>(
	removeCitations,
	fixOpenAILatexDelimiters,
);

export const messageContentFilters = new FilterHook<string>(
	removeCitations,
	fixOpenAILatexDelimiters,
);
